import React, { useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '@mantine/hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Group, Text, Button, Divider, ActionIcon, Center, Loader, Modal } from '@mantine/core';
import { notify } from './common';
import LoginLogoutApi from '../../api/LoginLogoutApi';
import SettingsApi from '../../api/SettingsApi';
import FunnelApi from '../../api/FunnelApi';
import { visiUserData, setVisiUserSession } from '../../GeneralFunction';
import { useAuth } from '../../AuthContext';
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconBook2,
  IconLifebuoy,
  IconHelpCircle,
} from '@tabler/icons-react';
import { useOnboardingStyles } from './onboardingStyles';
import { useOnbColors } from './onboardingColors';
import { OnboardingData, defaultOnboardingData, normalizeDomain, normalizePageUrl, domainHost } from './types';
import { Step1Domain } from './steps/Step1Domain';
import { Step2Script } from './steps/Step2Script';
import { Step3Subdomains } from './steps/Step3Subdomains';
import { Step4Funnel } from './steps/Step4Funnel';
import { Step5Webhooks } from './steps/Step5Webhooks';
import { Complete } from './steps/Complete';
import { GuidedSetup, GuidedIntegration } from './GuidedSetup';
import { WEBHOOK_INDEX, frameworkToPlatform } from './types';
import { SmartCodeNotDetectedModal } from '../../components/Funnel/SmartCodeNotDetectedModal';
import { SMART_CODE_GUIDE } from '../../components/GeneralComponent/smartCodeGuides';
import visioptLogo from '../../img/visiopt-small-logo.webp';

const STEP_COUNT = 6;
const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
const URL_RE = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;

export function Onboarding() {
  useDocumentTitle('Get Started · Visiopt');
  const { classes, cx } = useOnboardingStyles();
  const col = useOnbColors();
  const navigate = useNavigate();
  const { setWebsiteListData } = useAuth();
  const [searchParams] = useSearchParams();
  // Manual "Guided Setup" entry (from the account menu) — lets a completed user run it again
  const guided = searchParams.get('mode') === 'guided';
  // Local-only dev shortcut: ?step=5 jumps straight to a step. Ignored on any real host.
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const devStepParam = Number(searchParams.get('step'));
  const devStep =
    isLocalHost && searchParams.get('step') !== null && Number.isFinite(devStepParam)
      ? Math.max(0, Math.min(STEP_COUNT - 1, devStepParam - 1))
      : null;
  const [helpOpen, setHelpOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  // Funnel-page SmartCode gate: pages missing the code, and what still needs attaching
  const [smartOpen, setSmartOpen] = useState(false);
  const [smartMissing, setSmartMissing] = useState<string[]>([]);
  const funnelCtxRef = useRef<{
    systemWebsiteId: string;
    funnelId: string;
    entries: { url: string; pageId: string }[];
    saved: string[];
    allUrls: string[];
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const allowLeaveRef = useRef(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const lastSavedRef = useRef(0);
  // Host the website record was created with — changing it after pages exist would orphan them
  const savedDomainHostRef = useRef('');

  const [data, setData] = useState<OnboardingData>(defaultOnboardingData);

  // Hydrate the wizard from the server (source of truth) — never from localStorage.
  useEffect(() => {
    LoginLogoutApi.getonboardingdata()
      .then((res) => (res.ok ? res.json() : null))
      .then((res) => {
        if (res && res.status === 1 && res.data) {
          if (Number(res.data.onboarding_completed) === 1) {
            // Guided Setup always starts fresh with a new domain/funnel.
            if (guided) {
              setReady(true);
              return;
            }
            // Normal completed users go to the dashboard. Exception: a local ?step= jump
            // falls through to the hydration below so it reuses the existing domain/funnel.
            if (devStep === null) {
              navigate('/dashboard');
              return;
            }
          }
          const resumeStep = devStep !== null ? devStep : Math.max(0, Math.min(STEP_COUNT - 1, Number(res.data.onboarding_step) || 0));
          lastSavedRef.current = resumeStep;
          const d = res.data.domain;
          const f = res.data.funnel;
          if (d && d.url) savedDomainHostRef.current = domainHost(d.url);
          const funnelPages: string[] = f && Array.isArray(f.pages) ? f.pages.map(normalizePageUrl).filter(Boolean) : [];
          setData((prev) => ({
            ...prev,
            currentStep: resumeStep,
            // Verification state is persisted server-side so a resume doesn't force a re-verify
            scriptVerified: d ? Number(d.smartcode_verified) === 1 : prev.scriptVerified,
            scriptBypassed: d ? Number(d.smartcode_bypassed) === 1 : prev.scriptBypassed,
            domain: d
              ? {
                  ...prev.domain,
                  name: d.name || '',
                  url: d.url || '',
                  website_id: d.website_id || '',
                  type: frameworkToPlatform(d.front_end_framework, d.framework_type),
                  created: true,
                }
              : prev.domain,
            funnel: f
              ? {
                  ...prev.funnel,
                  name: f.name || '',
                  funnel_id: f.funnel_id || '',
                  created: true,
                  // Pages already attached: prefill the step and never re-add them
                  pages: funnelPages.length ? funnelPages : prev.funnel.pages,
                  savedUrls: funnelPages,
                }
              : prev.funnel,
          }));
        }
        setReady(true);
      })
      .catch(() => setReady(true));
    // eslint-disable-next-line
  }, []);

  // Keep the cached login data in sync so route guards let the dashboard through once complete.
  const syncLoginData = (stepNo: number, completed: boolean) => {
    try {
      const ud = visiUserData();
      if (ud) {
        ud.onboarding_step = stepNo;
        if (completed) ud.onboarding_completed = 1;
        localStorage.setItem('visiUserData', btoa(JSON.stringify(ud)));
      }
    } catch {
      // ignore
    }
  };

  // Persist progress after each step; completed=1 on the final screen also flips the flag
  const persistProgress = (n: number) => {
    const completed = n >= STEP_COUNT - 1;
    if (!completed && n <= lastSavedRef.current) return; // no forward progress → nothing to save
    lastSavedRef.current = Math.max(lastSavedRef.current, n);
    LoginLogoutApi.saveonboardingstep({ step: n, completed: completed ? 1 : 0 }).catch(() => {});
    syncLoginData(n, completed);
  };

  const step = data.currentStep;

  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const scroller = document.querySelector('.simplebar-content-wrapper');
        if (scroller) scroller.scrollTop = 0;
        window.scrollTo(0, 0);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [step]);

  // Guard against leaving onboarding by mistake. Browser Back -> custom confirm modal;
  // tab close / refresh -> native browser prompt (custom wording isn't allowed there).
  useEffect(() => {
    if (!ready) return;
    // Sentinel entry so the first browser Back lands back here instead of the previous page
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      if (allowLeaveRef.current) return;
      window.history.pushState(null, '', window.location.href);
      setExitOpen(true);
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [ready]);

  // Steps write straight into data; clear inline errors on edit
  const update = (patch: Partial<OnboardingData>) => {
    setErrors({});
    setData((prev) => ({ ...prev, ...patch }));
  };

  const goTo = (n: number) => {
    const clamped = Math.max(0, Math.min(STEP_COUNT - 1, n));
    setData((prev) => ({ ...prev, currentStep: clamped }));
    persistProgress(clamped);
  };

  // Validate the active step before advancing
  const validateStep = (): boolean => {
    const e: Record<string, string> = {};
    if (step === 0) {
      if (!data.domain.name.trim()) e.name = 'Domain name is required.';
      const cleanUrl = data.domain.url.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
      if (!cleanUrl) e.url = 'Domain URL is required.';
      else if (!DOMAIN_RE.test(cleanUrl)) e.url = 'Enter a valid domain, e.g. example.com';
      // Funnel pages are validated against the website domain, so changing it would orphan them
      else if (
        savedDomainHostRef.current &&
        (data.funnel.savedUrls || []).length > 0 &&
        domainHost(data.domain.url) !== savedDomainHostRef.current
      ) {
        e.url = `You already added funnel pages on ${savedDomainHostRef.current}. Remove those pages before changing your domain.`;
      }
    }
    if (step === 1 && !data.domain.type) {
      e.type = 'Select your platform to get the right script.';
    }
    if (step === 3) {
      const landing = (data.funnel.pages[0] || '').trim();
      if (!data.funnel.name.trim()) e.funnelName = 'Funnel name is required.';
      if (!landing) e.landingUrl = 'Landing page URL is required.';
      else if (!URL_RE.test(landing)) e.landingUrl = 'Enter a valid URL.';
      // The same page must not appear twice in the funnel
      const seen = new Set<string>();
      data.funnel.pages.forEach((p, i) => {
        const u = normalizePageUrl(p);
        if (!u) return;
        if (seen.has(u)) {
          e[i === 0 ? 'landingUrl' : `page_${i}`] = 'This page is already in your funnel. Please remove the duplicate.';
        } else {
          seen.add(u);
        }
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const toastFor = (s: number) => {
    const map: Record<number, { title: string; message: string }> = {
      0: { title: 'Domain saved', message: `${data.domain.url || data.domain.name} is ready to track.` },
      1:
        data.domain.type === 'shopify' && data.shopifyConnected
          ? { title: 'Shopify connected', message: 'The Visiopt app will keep your Smart Code installed.' }
          : data.scriptVerified
          ? { title: 'Tracking script ready', message: 'Smart Code detected on your website.' }
          : { title: 'Verification skipped', message: "We'll keep checking your website in the background." },
      3: { title: 'Funnel created', message: `"${data.funnel.name}" is ready to track.` },
    };
    const t = map[s];
    if (t) notify('success', t.message, t.title);
  };

  // Step 1: normalize the domain, create/update the website record, keep website_id
  const createWebsite = () => {
    const siteName = data.domain.name.trim();
    const normalized = normalizeDomain(data.domain.url);
    setSaving(true);
    SettingsApi.addeditdomain({
      site_type: 0,
      site_name: siteName,
      website_address: normalized,
      front_end_framework: 0,
      website_id: data.domain.website_id || '',
    })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        setSaving(false);
        if (res && res.status === 1 && res.data && res.data.site_id) {
          savedDomainHostRef.current = domainHost(normalized);
          setData((prev) => ({
            ...prev,
            domain: { ...prev.domain, url: normalized, website_id: res.data.site_id, created: true },
          }));
          toastFor(0);
          goTo(1);
        } else {
          setErrors({ url: res && res.message ? res.message : 'Could not save your website. Please try again.' });
        }
      })
      .catch(() => {
        setSaving(false);
        setErrors({ url: 'Could not save your website. Please try again.' });
      });
  };

  // Attach [url, pageId] pairs to the funnel in a single request. force=true bypasses the SmartCode
  // check (Continue Anyway). Returns which URLs attached and which are still missing the SmartCode.
  const attachPages = async (
    systemWebsiteId: string,
    entries: { url: string; pageId: string }[],
    force: boolean
  ): Promise<{ ok: boolean; attached: string[]; missing: string[] }> => {
    if (entries.length === 0) return { ok: true, attached: [], missing: [] };
    const res = await FunnelApi.addtofunnel({
      system_website_id: systemWebsiteId,
      page_ids: entries.map((e) => e.pageId),
      ...(force ? { force_add: 1 } : {}),
    }).then((r: Response) => (r.ok ? r.json() : null));

    // ok = the batch request itself succeeded; distinguishes a network/server failure from
    // "the server processed it but the page is still missing the SmartCode"
    const ok = Boolean(res && res.status === 1 && res.data && Array.isArray(res.data.results));
    const results: any[] = ok ? res.data.results : [];
    const urlById = new Map(entries.map((e) => [e.pageId, e.url]));
    const attached: string[] = [];
    for (const r of results) {
      const url = urlById.get(r.page_id);
      if (url && r.status === 1) attached.push(url);
    }
    const missing = entries.filter((e) => !attached.includes(e.url)).map((e) => e.url);
    return { ok, attached, missing };
  };

  // Persist funnel state and advance to the webhooks step
  const finalizeFunnel = (funnelId: string, urls: string[], saved: string[]) => {
    setData((prev) => ({
      ...prev,
      funnel: { ...prev.funnel, funnel_id: funnelId, pages: urls.length ? urls : prev.funnel.pages, savedUrls: saved, created: true },
    }));
    toastFor(3);
    goTo(4);
  };

  // Step 4: create the funnel, then create all pages in one request and attach them.
  // Idempotent — a re-entered step reuses the saved funnel_id and skips saved URLs.
  const createFunnel = async () => {
    const systemWebsiteId = data.domain.website_id;
    if (!systemWebsiteId) {
      setErrors({ funnelName: 'Please complete the website step first.' });
      return;
    }
    const ud = visiUserData();
    const funnelLimit = ud && ud.no_of_add_access ? ud.no_of_add_access.no_of_funnels_access : 1000;
    const pageLimit = ud && ud.no_of_add_access ? ud.no_of_add_access.no_of_pages_access : 1000;
    // Page URLs are normalized here; the server enforces that they belong to this
    // website or one of its related domains (add_page).
    // De-duplicate so the same URL is never created/attached twice (validateStep also blocks this)
    const allUrls = Array.from(new Set(data.funnel.pages.map(normalizePageUrl).filter(Boolean)));
    const saved = [...(data.funnel.savedUrls || [])];
    const toCreate = allUrls.filter((u) => !saved.includes(u));
    setSaving(true);
    try {
      let funnelId = data.funnel.funnel_id || '';
      const funnelRes = await FunnelApi.addfunnel({
        system_website_id: systemWebsiteId,
        name: data.funnel.name.trim(),
        // Renaming an existing funnel must not trip the "max funnels" check
        account_funnel_limit: funnelId ? 1000 : funnelLimit,
        ...(funnelId ? { funnel_id: funnelId } : {}),
      }).then((r: Response) => (r.ok ? r.json() : null));
      if (!funnelRes || funnelRes.status !== 1 || !funnelRes.data) {
        setSaving(false);
        setErrors({ funnelName: funnelRes && funnelRes.message ? funnelRes.message : 'Could not create your funnel. Please try again.' });
        return;
      }
      funnelId = funnelRes.data.selected_funnel_id;
      // Persist the funnel id right away so a retry after a later failure (e.g. a bad page URL)
      // reuses this funnel (rename) instead of creating it again and hitting "name already exists"
      setData((prev) => ({ ...prev, funnel: { ...prev.funnel, funnel_id: funnelId, created: true } }));

      // Nothing new to add (funnel rename / resumed step) → just advance
      if (toCreate.length === 0) {
        setSaving(false);
        finalizeFunnel(funnelId, allUrls, saved);
        return;
      }

      // 1) Create every page in a single add_page request (array of URLs)
      const pageRes = await FunnelApi.addpage({
        system_website_id: systemWebsiteId,
        urls: toCreate,
        no_of_pages_access: pageLimit,
      }).then((r: Response) => (r.ok ? r.json() : null));

      if (!pageRes || pageRes.status !== 1 || !pageRes.data || !Array.isArray(pageRes.data.results)) {
        setSaving(false);
        setErrors({ landingUrl: (pageRes && pageRes.message) || 'Could not add your pages. Please try again.' });
        return;
      }
      const results: any[] = pageRes.data.results;
      const entries: { url: string; pageId: string }[] = [];
      const failures: { url: string; message: string }[] = [];
      // Two different-looking URLs can resolve to the same page server-side (trailing slash, case,
      // www, index.html…). add_page returns the same page_id for them, so keep each page only once.
      const seenPageIds = new Set<string>();
      for (const r of results) {
        // A page that already exists is reused rather than treated as a failure
        const exists = r && r.status !== 1 && r.error_code === 'page_exists';
        const pageId = r && r.data ? r.data.page_id : '';
        if (r && (r.status === 1 || exists) && pageId) {
          if (!seenPageIds.has(pageId)) {
            seenPageIds.add(pageId);
            entries.push({ url: r.url, pageId });
          }
        } else {
          failures.push({ url: (r && r.url) || '', message: (r && r.message) || 'This page could not be added.' });
        }
      }

      // 2) Surface domain-mismatch / other page errors under the specific page that failed
      if (failures.length > 0) {
        setSaving(false);
        const errObj: Record<string, string> = {};
        for (const f of failures) {
          const idx = data.funnel.pages.findIndex((p) => normalizePageUrl(p) === f.url);
          errObj[idx <= 0 ? 'landingUrl' : `page_${idx}`] = f.message;
        }
        setErrors(errObj);
        notify('error', failures.map((f) => f.message).join(' '), 'Some pages could not be added');
        return;
      }

      // 3) SmartCode gate: attach without force; collect pages whose SmartCode isn't detected
      const { ok, attached, missing } = await attachPages(systemWebsiteId, entries, false);
      // A failed request is not "SmartCode missing" — surface it instead of popping the modal
      if (!ok) {
        setSaving(false);
        setErrors({ landingUrl: 'Could not add your pages to the funnel. Please try again.' });
        notify('error', 'Could not add your pages to the funnel. Please try again.', 'Something went wrong');
        return;
      }
      const newSaved = [...saved, ...attached];

      if (missing.length > 0) {
        funnelCtxRef.current = {
          systemWebsiteId,
          funnelId,
          entries: entries.filter((e) => missing.includes(e.url)),
          saved: newSaved,
          allUrls,
        };
        setSaving(false);
        setSmartMissing(missing);
        setSmartOpen(true);
        return;
      }

      setSaving(false);
      finalizeFunnel(funnelId, allUrls, newSaved);
    } catch {
      setSaving(false);
      setErrors({ funnelName: 'Could not create your funnel. Please try again.' });
    }
  };

  // Re-check the missing pages (no force). Resolves true once they all have the SmartCode.
  const smartRecheck = async (): Promise<boolean> => {
    const ctx = funnelCtxRef.current;
    if (!ctx) return true;
    const { ok, attached, missing } = await attachPages(ctx.systemWebsiteId, ctx.entries, false);
    // Request failed — keep the modal open, report "still not detected" rather than advancing
    if (!ok) return false;
    const saved = [...ctx.saved, ...attached];
    if (missing.length === 0) {
      setSmartOpen(false);
      finalizeFunnel(ctx.funnelId, ctx.allUrls, saved);
      return true;
    }
    ctx.entries = ctx.entries.filter((e) => missing.includes(e.url));
    ctx.saved = saved;
    setSmartMissing(missing);
    return false;
  };

  // User accepted the missing SmartCode — force-add the remaining pages, then finish
  const smartContinueAnyway = async (): Promise<boolean> => {
    const ctx = funnelCtxRef.current;
    if (!ctx) return true;
    const { ok, attached } = await attachPages(ctx.systemWebsiteId, ctx.entries, true);
    // Force-add failed — do NOT advance; keep the modal so the user can retry
    if (!ok) {
      notify('error', 'Could not add your pages to the funnel. Please try again.', 'Something went wrong');
      return false;
    }
    const saved = [...ctx.saved, ...attached];
    setSmartOpen(false);
    finalizeFunnel(ctx.funnelId, ctx.allUrls, saved);
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (step === 0) {
      createWebsite();
      return;
    }
    if (step === 3) {
      createFunnel();
      return;
    }
    // Webhooks step: no selection → straight to complete; otherwise run guided setup
    if (step === 4) {
      if (data.webhooks.length === 0) {
        goTo(5);
      } else {
        setGuideOpen(true);
      }
      return;
    }
    toastFor(step);
    goTo(step + 1);
  };

  const guidedIntegrations = data.webhooks
    .map((w) => {
      const opt = WEBHOOK_INDEX[w.id];
      return opt ? { ...opt, name: w.name } : null;
    })
    .filter(Boolean) as GuidedIntegration[];

  const leaveOnboarding = () => {
    allowLeaveRef.current = true;
    setExitOpen(false);
    window.history.go(-2);
  };

  // First step has no earlier step, so Back exits onboarding to the previous page
  const handleBack = () => {
    if (step === 0) {
      leaveOnboarding();
      return;
    }
    goTo(step - 1);
  };

  // Idempotent finish. Only navigates once the server confirms completion, otherwise
  // the user would land on the dashboard and be bounced back on their next login.
  const goToDashboard = (to?: string) => {
    setSaving(true);
    LoginLogoutApi.saveonboardingstep({ step: STEP_COUNT - 1, completed: 1 })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        setSaving(false);
        if (res && res.status === 1) {
          syncLoginData(STEP_COUNT - 1, true);
          if (data.domain.website_id) {
            setVisiUserSession('website', data.domain.website_id);
            setVisiUserSession('funnel', data.funnel.funnel_id || '');
            setVisiUserSession('page', '');
          }
          setWebsiteListData([]);
          notify('success', 'Welcome to Visiopt', 'Setup complete');
          navigate(to ? `/${to}` : '/dashboard');
        } else {
          notify('error', 'We could not save your setup. Please try again.', 'Setup not saved');
        }
      })
      .catch(() => {
        setSaving(false);
        notify('error', 'We could not save your setup. Please try again.', 'Setup not saved');
      });
  };

  const stepProps = { data, update, errors, saving, goToDashboard, goNext: () => goTo(step + 1) };

  const renderStep = () => {
    switch (step) {
      case 0:
        return <Step1Domain {...stepProps} />;
      case 1:
        return <Step2Script {...stepProps} />;
      case 2:
        return <Step3Subdomains {...stepProps} />;
      case 3:
        return <Step4Funnel {...stepProps} />;
      case 4:
        return <Step5Webhooks {...stepProps} />;
      default:
        return <Complete {...stepProps} />;
    }
  };

  const isComplete = step === STEP_COUNT - 1;

  // Wait for server hydration so we don't flash step 1 with empty fields before the resume
  if (!ready) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Loader  color="grape" />
      </Center>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header — logo left, Need Help right */}
      <div className={classes.header}>
        <img className={classes.topLogo} src={visioptLogo} alt="Visiopt" />
        <button className={classes.needHelpBtn} onClick={() => setHelpOpen((v) => !v)}>
          <IconHelpCircle size={17} /> Need Help?
        </button>
      </div>

      {/* Progress tracker (dots only) */}
      <div className={classes.tracker}>
        {Array.from({ length: STEP_COUNT }).map((_, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <React.Fragment key={i}>
              <div
                className={cx(classes.bubble, {
                  [classes.bubbleActive]: active,
                  [classes.bubbleDone]: done,
                })}
                style={done ? { cursor: 'pointer' } : undefined}
                onClick={done ? () => goTo(i) : undefined}
                title={done ? 'Go back to this step' : undefined}
              >
                {done ? <IconCheck size={16} /> : i + 1}
              </div>
              {i < STEP_COUNT - 1 && (
                <div className={classes.trackerLine}>
                  <div
                    className={classes.trackerLineFill}
                    style={{ transform: `scaleX(${i < step ? 1 : 0})` }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Active step card */}
      <Box className={classes.card} key={step}>
        {renderStep()}
      </Box>

      {/* Bottom navigation — hidden on the completion screen */}
      {!isComplete && (
        <div className={classes.bottomNav}>
          <Button
            className={classes.navBack}
            variant="default"
            disabled={saving}
            leftIcon={<IconChevronLeft size={16} />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            className={classes.navNext}
            rightIcon={!saving ? <IconChevronRight size={16} /> : undefined}
            loading={saving}
            disabled={
              step === 1 &&
              // Shopify is gated on the app connection, every other platform on verification
              (data.domain.type === 'shopify'
                ? !data.shopifyConnected
                : !data.scriptVerified && !data.scriptBypassed)
            }
            onClick={handleNext}
          >
            {step === 4 ? 'Start Using Visiopt' : step === 2 ? 'Continue' : 'Next'}
          </Button>
        </div>
      )}

      {/* Need Help panel triggered by the bottom-left dots */}
      {helpOpen && (
        <Box className={classes.helpPanel}>
          <Group position="apart" mb={6}>
            <Text fw={700} fz="sm" c={col.textTitle}>
              Need a hand?
            </Text>
            <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => setHelpOpen(false)}>
              <IconX size={16} />
            </ActionIcon>
          </Group>
          <Text fz={13} c="dimmed" mb={10}>
            We're here to help you finish setup in a few minutes.
          </Text>
          <Divider mb={8} />
          <a className={classes.helpLink} href="https://visiopt.com/help-videos/settings.html" target="_blank" rel="noreferrer">
            <IconBook2 size={18} /> Installation documentation
          </a>
          <a className={classes.helpLink} href="mailto:support@visiopt.com">
            <IconLifebuoy size={18} /> Contact support
          </a>
        </Box>
      )}

      {/* Guided integration setup (confirmation + wizard) */}
      <GuidedSetup
        opened={guideOpen}
        integrations={guidedIntegrations}
        funnelName={data.funnel.name}
        systemWebsiteId={data.domain.website_id}
        funnelId={data.funnel.funnel_id || ''}
        onFunnelRenamed={(name) => setData((prev) => ({ ...prev, funnel: { ...prev.funnel, name } }))}
        onClose={() => setGuideOpen(false)}
        onComplete={() => {
          setGuideOpen(false);
          goTo(5);
        }}
        onSkip={() => {
          setGuideOpen(false);
          goTo(5);
        }}
      />

      {/* Browser Back confirmation — leaving terminates onboarding */}
      <Modal
        opened={exitOpen}
        onClose={() => setExitOpen(false)}
        centered
        withCloseButton={false}
        title={<Text fw={700} c={col.textTitle}>Leave onboarding?</Text>}
      >
        <Text fz={14} c={col.textPrimary}>
          Going back will terminate the onboarding process. Are you sure you want to go back?
        </Text>
        <Group position="right" mt="lg">
          <Button variant="default" onClick={() => setExitOpen(false)}>
            No
          </Button>
          <Button color="red" onClick={leaveOnboarding}>
            Yes
          </Button>
        </Group>
      </Modal>

      {/* SmartCode gate for funnel pages — same popup used on the Funnel page */}
      <SmartCodeNotDetectedModal
        opened={smartOpen}
        pageUrls={smartMissing}
        guideUrl={SMART_CODE_GUIDE[data.domain.type] || SMART_CODE_GUIDE.custom}
        onClose={() => setSmartOpen(false)}
        onRecheck={smartRecheck}
        onContinueAnyway={smartContinueAnyway}
      />
    </Box>
  );
}
